/**
 * DeleteCommentTemplateDialog Component
 *
 * Confirmation dialog for deleting a comment template.
 * Displays the template name and asks for user confirmation before deletion.
 *
 * @param template - The comment template to delete
 * @param open - Controls dialog visibility
 * @param onOpenChange - Callback when dialog open state changes
 *
 * @example
 * ```tsx
 * <DeleteCommentTemplateDialog
 *   template={deletingTemplate}
 *   open={deleteDialogOpen}
 *   onOpenChange={setDeleteDialogOpen}
 * />
 * ```
 */

import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDeleteCommentTemplate } from '@/features/comment-template/hooks'
import { ResponseCommentTemplate } from '@/schema/commentTemplate'

interface DeleteCommentTemplateDialogProps {
  template: ResponseCommentTemplate | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const DeleteCommentTemplateDialog: React.FC<
  DeleteCommentTemplateDialogProps
> = ({ template, open, onOpenChange }) => {
  const { t } = useTranslation()
  const deleteMutation = useDeleteCommentTemplate()

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen)
  }

  const handleConfirm = () => {
    if (!template) return

    deleteMutation.mutate(template.id, {
      onSuccess: () => {
        triggerToast(
          'success',
          t('commentTemplate.dialog.messages.deleteSuccess')
        )
        onOpenChange(false)
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message?: string }>
        const errorMessage =
          axiosError.response?.data?.message ||
          axiosError.message ||
          t('commentTemplate.dialog.messages.errorGeneric')

        triggerToast('error', errorMessage)
      },
    })
  }

  const isDeleting = useMemo(
    () => deleteMutation.isPending,
    [deleteMutation.isPending]
  )

  if (!template) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="sm:max-w-[480px] bg-white shadow-xl rounded-2xl p-6">
          <DialogHeader className="space-y-0.5">
            <DialogTitle className="m-0 font-semibold text-md text-primary-900">
              {t('commentTemplate.dialog.deleteTitle')}
            </DialogTitle>
            <DialogDescription className="text-sm text-tertiary-600">
              {t('commentTemplate.dialog.deleteDescription')}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3">
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid bg-white"
              variant="default"
              onClick={() => handleOpenChange(false)}
              disabled={isDeleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid border-transparent !text-white !bg-error-solid"
              variant="default"
              onClick={handleConfirm}
              disabled={isDeleting}
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
